import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  FileText,
  CreditCard,
  Plane,
  GraduationCap,
  HeartHandshake,
  UserCheck,
  Calendar,
  MapPin,
  CheckCircle2,
  ChevronDown,
  UploadCloud,
  ClipboardList,
} from 'lucide-react';
import Layout from '../layout/Layout';

export default function ScholarshipGrantDetails() {
  const coverageItems = [
    { title: 'تجهيز الأوراق', description: 'بنساعدك تراجع وتجهز مستنداتك للتقديم.', icon: FileText },
    { title: 'مصاريف التقديم', description: 'بنتحمل مصاريف تقديم طلبك للجامعة.', icon: CreditCard },
    { title: 'تكاليف السفر', description: 'بنغطي تكاليف سفرك عشان تبدأ دراستك في أوروبا.', icon: Plane },
    { title: 'تكاليف الدراسة', description: 'بنغطي تكاليف دراستك في الجامعة.', icon: GraduationCap },
    { title: 'الدعم خلال رحلة التقديم', description: 'فريق EduNasser معاك خطوة بخطوة من أول ما تقدم.', icon: HeartHandshake },
  ];

  const eligibilityItems = [
    { title: 'الطلاب والخريجين', description: 'طالب ثانوية، حاصل على الثانوية، طالب جامعة، أو خريج جامعة.', icon: UserCheck },
    { title: 'السن وقت التقديم', description: 'من 15 سنة لـ 45 سنة.', icon: Calendar },
    { title: 'المحافظة', description: 'التقديم مفتوح من كل محافظات مصر.', icon: MapPin },
  ];

  const documents = [
    { title: 'شهادة الثانوية أو شهادة التخرج', details: 'حسب مؤهلك: شهادة الثانوية، أو شهادة التخرج لو خريج جامعة.' },
    { title: 'صورة البطاقة — الوجه الأمامي', details: 'صورة واضحة لوش البطاقة الشخصية.' },
    { title: 'صورة البطاقة — الوجه الخلفي', details: 'صورة واضحة لضهر البطاقة الشخصية.' },
  ];

  const steps = [
    { number: '1', title: 'بياناتك', description: 'اسمك بالكامل، إيميلك، رقم موبايلك، تاريخ ميلادك، ومحافظتك.' },
    { number: '2', title: 'دراستك', description: 'مؤهلك الدراسي، تفاصيل دراستك، والتخصص اللي حابب تدرسه.' },
    { number: '3', title: 'المستندات', description: 'ترفع التلات مستندات من موبايلك، PDF أو صورة واضحة.' },
    { number: '4', title: 'راجع وقدّم', description: 'تراجع بياناتك وتبعت الطلب، وييجيلك رقم الطلب على طول.' },
  ];

  const nextSteps = [
    'هيظهرلك رقم الطلب على الشاشة — احفظه معاك.',
    'هيوصلك إيميل بتأكيد استلام طلبك.',
    'فريق EduNasser هيراجع بياناتك والمستندات اللي رفعتها.',
    'لو تم اختيارك للمرحلة الجاية، هنتواصل معاك على رقم الموبايل أو البريد الإلكتروني اللي سجلتهم.',
  ];

  const faqs = [
    { q: 'محتاج أعمل حساب أو أسجل؟', a: 'لأ، التقديم مباشر من غير تسجيل ولا حساب ولا كلمة سر.' },
    { q: 'أقدر أقدم من الموبايل؟', a: 'أيوه، تقدر تملى الطلب وترفع المستندات كلها من موبايلك.' },
    { q: 'الأوراق محتاجة ترجمة؟', a: 'لأ، مش محتاج تترجم الورق في المرحلة دي. المهم الصور تكون واضحة وسارية.' },
    { q: 'أقدم تاني لو قدمت قبل كده؟', a: 'مش محتاج. لو قدمت قبل كده بنفس بيانات التواصل، هيظهرلك نفس رقم الطلب.' },
    { q: 'هتتواصلوا معايا إزاي؟', a: 'لو تم اختيارك للمرحلة الجاية، هنتواصل معاك على رقم الموبايل أو البريد الإلكتروني اللي سجلتهم.' },
  ];

  return (
    <Layout>
      <div dir="rtl" lang="ar" className="min-h-screen bg-[#FAF9FD] text-[#2C2C2C] pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 sm:pt-36">
          {/* 1. Header / Navigation */}
          <header className="mb-12">
            <Link
              to="/scholarship"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#5B3FD0] hover:text-[#4729b0] min-h-[44px] py-2 px-1 transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
              <span>الرجوع لصفحة التقديم</span>
            </Link>

            <div className="mt-4">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#5B3FD0]/10 text-[#5B3FD0] text-xs sm:text-sm font-semibold mb-4">
                <span>دليل المنحة الكامل</span>
              </div>
              <h1 className="text-2xl sm:text-4xl font-extrabold text-[#2C2C2C] tracking-tight leading-tight mb-3">
                تفاصيل منحة EduNasser
              </h1>
              <p className="text-base sm:text-lg text-[#707070] leading-relaxed">
                كل اللي محتاج تعرفه عن منحة EduNasser للدراسة في أوروبا، الشروط، الأوراق المطلوبة، وخطوات التقديم.
              </p>
            </div>
          </header>

          <div className="space-y-12 sm:space-y-16">
            {/* 2. المنحة دي إيه؟ */}
            <section className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 shadow-xs">
              <h2 className="text-xl sm:text-2xl font-bold text-[#2C2C2C] mb-4">
                المنحة دي إيه؟
              </h2>
              <div className="space-y-3 text-base text-[#707070] leading-relaxed">
                <p>
                  منحة EduNasser هي فرصة ممولة بالكامل موجهة للطلاب المصريين المتفوقين اللي عندهم طموح يكملوا دراستهم في أوروبا.
                </p>
                <p>
                  الهدف الأساسي إننا نفتحلك الطريق الأكاديمي، ونشيل عنك عبء التكاليف وإجراءات التقديم والقبول المعقدة.
                </p>
                <p>
                  التقديم مفتوح ومباشر للجميع بدون أي حسابات مسبقة وبدون دفع أي مصاريف.
                </p>
              </div>
            </section>

            {/* 3. المنحة بتغطي إيه؟ */}
            <section>
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-[#2C2C2C] mb-2">
                  المنحة بتغطي إيه؟
                </h2>
                <p className="text-base text-[#707070] leading-relaxed">
                  المنحة بتوفر دعم شامل بيغطي رحلتك من بدايتها:
                </p>
              </div>
              <div className="space-y-3">
                {coverageItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 flex items-start gap-4 transition-colors hover:border-[#5B3FD0]/30"
                    >
                      <div className="w-11 h-11 rounded-xl bg-[#5B3FD0]/10 text-[#5B3FD0] flex items-center justify-center shrink-0 mt-0.5">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-bold text-[#2C2C2C] mb-1">
                          {item.title}
                        </h3>
                        <p className="text-sm sm:text-base text-[#707070] leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* 4. مين يقدر يقدم؟ */}
            <section>
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-[#2C2C2C] mb-2">
                  مين يقدر يقدم؟
                </h2>
                <p className="text-base text-[#707070] leading-relaxed">
                  شروط الأهلية واضحة ومحددة حسب نموذج التقديم:
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {eligibilityItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 flex flex-col gap-2.5"
                    >
                      <div className="w-10 h-10 rounded-xl bg-[#5B3FD0]/10 text-[#5B3FD0] flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <h3 className="text-base font-bold text-[#2C2C2C]">
                        {item.title}
                      </h3>
                      <p className="text-sm sm:text-base text-[#707070] leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* 5. المستندات اللي هتحتاجها */}
            <section className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7">
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-[#2C2C2C] mb-2">
                  المستندات اللي هتحتاجها
                </h2>
                <p className="text-base text-[#707070] leading-relaxed">
                  جهز الأوراق دي على جهازك أو موبايلك قبل ما تبدأ التقديم:
                </p>
              </div>
              <div className="space-y-4 mb-6">
                {documents.map((doc, idx) => (
                  <div key={doc.title} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#5B3FD0]/10 text-[#5B3FD0] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-[#2C2C2C]">
                        {doc.title}
                      </h3>
                      <p className="text-sm sm:text-base text-[#707070] leading-relaxed mt-0.5">
                        {doc.details}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-[#FAF9FD] rounded-xl p-4 border border-[#5B3FD0]/15 flex items-start gap-3">
                <UploadCloud className="w-5 h-5 text-[#5B3FD0] shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-[#707070] leading-relaxed">
                  شروط الملفات: نقبل ملفات PDF أو صور (JPG, JPEG, PNG) بشرط حجم الملف الواحد ما يعديش 20 ميجابايت.
                </p>
              </div>
            </section>

            {/* 6. التقديم بيتم إزاي؟ */}
            <section>
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-[#2C2C2C] mb-2">
                  التقديم بيتم إزاي؟
                </h2>
                <p className="text-base text-[#707070] leading-relaxed">
                  التقديم بيمر بـ 4 خطوات سريعة وبسيطة:
                </p>
              </div>
              <div className="space-y-3">
                {steps.map((step) => (
                  <div
                    key={step.number}
                    className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 flex items-start gap-4"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#5B3FD0] text-white font-bold flex items-center justify-center shrink-0">
                      {step.number}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-[#2C2C2C] mb-1">
                        {step.title}
                      </h3>
                      <p className="text-sm sm:text-base text-[#707070] leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm sm:text-base text-[#707070] leading-relaxed">
                مش محتاج تعمل حساب ولا تسجل دخول، التقديم كله بيخلص في دقايق معدودة.
              </p>
            </section>

            {/* 7. وبعد ما تقدم؟ */}
            <section className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7">
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-[#2C2C2C] mb-2">
                  وبعد ما تقدم؟
                </h2>
                <p className="text-base text-[#707070] leading-relaxed">
                  دي الخطوات والمتابعة اللي بتحصل بعد إرسال طلبك:
                </p>
              </div>
              <div className="space-y-3.5">
                {nextSteps.map((txt) => (
                  <div key={txt} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#5B3FD0] shrink-0 mt-0.5" />
                    <p className="text-sm sm:text-base text-[#707070] leading-relaxed">
                      {txt}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* 8. أسئلة شائعة */}
            <section>
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-[#2C2C2C] mb-2">
                  أسئلة شائعة
                </h2>
                <p className="text-base text-[#707070] leading-relaxed">
                  إجابات سريعة على أكتر الاستفسارات اللي بتوصلنا:
                </p>
              </div>
              <div className="space-y-3">
                {faqs.map((faq) => (
                  <details
                    key={faq.q}
                    className="group bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 transition-colors open:border-[#5B3FD0]/30"
                  >
                    <summary className="flex items-center justify-between gap-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden font-bold text-[#2C2C2C] text-base min-h-[44px]">
                      <span>{faq.q}</span>
                      <ChevronDown className="w-5 h-5 text-[#5B3FD0] shrink-0 transition-transform duration-200 group-open:rotate-180" />
                    </summary>
                    <p className="mt-3 text-base text-[#707070] leading-relaxed pt-3 border-t border-gray-100">
                      {faq.a}
                    </p>
                  </details>
                ))}
              </div>
            </section>

            {/* 9. Closing CTA */}
            <section className="bg-[#5B3FD0]/5 border border-[#5B3FD0]/20 rounded-2xl p-6 sm:p-8 text-center">
              <div className="w-12 h-12 rounded-2xl bg-[#5B3FD0]/10 text-[#5B3FD0] flex items-center justify-center mx-auto mb-4">
                <ClipboardList className="w-6 h-6" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-[#2C2C2C] mb-2">
                جاهز تبدأ؟
              </h2>
              <p className="text-base text-[#707070] max-w-md mx-auto leading-relaxed mb-6">
                التقديم بياخد دقايق، جهز أوراقك وابدأ دلوقتي.
              </p>
              <Link
                to="/scholarship#apply"
                className="inline-flex items-center justify-center gap-2.5 px-8 py-3.5 text-base font-bold text-white bg-[#5B3FD0] hover:bg-[#4729b0] rounded-xl shadow-md hover:shadow-lg transition-all min-h-[48px] w-full sm:w-auto"
              >
                <span>قدم دلوقتي</span>
                <ArrowLeft className="w-4 h-4" />
              </Link>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
}
